import { Injectable } from '@angular/core';
import { DateUtils } from '../../shared';
import { TaxRate } from '../models/invoicing/tax';
import { TaxRateType } from '../models/invoicing/tax-rate-type.enum';

@Injectable()
export class TaxService {
  getCurrentTaxRate(taxRates: TaxRate[], localDate = DateUtils.dateToIsoDateOnly(new Date())): TaxRate | null {
    if (!taxRates?.length) {
      return null;
    }

    const filteredTaxRates = taxRates.filter(item => item.effectiveDate && item.effectiveDate <= localDate);
    return this.sortAndCleanTaxRates(filteredTaxRates)[0];
  }

  getTaxRateType(
    taxRate: TaxRate,
    taxRates: TaxRate[],
    localDate = DateUtils.dateToIsoDateOnly(new Date()),
  ): TaxRateType {
    const taxRateEffectiveDate = taxRate.effectiveDate?.split('T')[0];
    if (taxRateEffectiveDate && taxRateEffectiveDate > localDate) {
      return TaxRateType.Upcoming;
    }

    const currentTaxRate = this.getCurrentTaxRate(taxRates, localDate);

    if (taxRate.id === currentTaxRate?.id) {
      return TaxRateType.Current;
    }

    return TaxRateType.Ended;
  }

  sortAndCleanTaxRates(taxRates: TaxRate[]): TaxRate[] {
    if (!taxRates?.length) {
      return [];
    }
    return [...taxRates]
      .filter(item => item.effectiveDate)
      .sort((a: TaxRate, b: TaxRate) => {
        const timeA = a.effectiveDate?.split('T')[0];
        const timeB = b.effectiveDate?.split('T')[0];
        return (timeB ?? '').localeCompare(timeA ?? '');
      });
  }

  getUpcomingFromLocalDate(taxRates: TaxRate[], localDate = DateUtils.dateToIsoDateOnly(new Date())): TaxRate | null {
    const currentRate = this.getCurrentTaxRate(taxRates, localDate);

    if (!currentRate) {
      return null;
    }

    return this.getUpcomingFromCurrent(currentRate, taxRates);
  }

  getPastFromLocalDate(taxRates: TaxRate[], localDate = DateUtils.dateToIsoDateOnly(new Date())): TaxRate | null {
    const currentRate = this.getCurrentTaxRate(taxRates, localDate);

    return !currentRate ? null : this.getPastFromCurrent(currentRate, taxRates);
  }

  getUpcomingFromCurrent(currentTaxRate: TaxRate, taxRates: TaxRate[]): TaxRate | null {
    return this.getNextOrPrevTaxRate(false, currentTaxRate, taxRates);
  }

  getPastFromCurrent(currentTaxRate: TaxRate, taxRates: TaxRate[]): TaxRate | null {
    return this.getNextOrPrevTaxRate(true, currentTaxRate, taxRates);
  }

  private getNextOrPrevTaxRate(isPrev: boolean, currentRate: TaxRate, taxRates: TaxRate[]): TaxRate | null {
    const sortedTaxRates = this.sortAndCleanTaxRates(taxRates);
    if (!sortedTaxRates?.length || sortedTaxRates.length === 1 || !currentRate) {
      return null;
    }

    const currentRateIndex = sortedTaxRates.findIndex(rate => rate.id === currentRate.id);

    if (isPrev) {
      // if it's last then there is no previous (array sorted future to past)
      return currentRateIndex >= sortedTaxRates.length - 1 ? null : sortedTaxRates[currentRateIndex + 1];
    } else {
      // if it's first then there is no next (array sorted future to past)
      return currentRateIndex === 0 ? null : sortedTaxRates[currentRateIndex - 1];
    }
  }
}
